/**
 * The Inspector's "Controller" zone and its picker (DEC-256).
 *
 * The zone lists what OPBridge's controllers do with this item - read from
 * the registry every time, since the binding lives in OPBridge, not here -
 * with a Remove on each, and "Assign a controller..." to add one.
 *
 * The picker (a Navigate-kit sheet, DEC-255, body-portalled because it opens
 * from the Inspector, see InfoHint) answers three questions on one screen:
 * what should it do (only when the item does more than one thing), which
 * control (PRESS IT: OPBridge's `control` event names it; or pick it from
 * the list), and on which page (only when OPBridge has more than one).
 * Controls that cannot do the chosen thing are not offered (`fits`); a press
 * on one says why instead of being taken.
 *
 * Novice path: tap "Assign a controller...", press the pedal, tap Assign.
 */

import { For, Show, createMemo, createResource, createSignal, onCleanup } from 'solid-js';
import { Portal } from 'solid-js/web';
import { projects, type LayoutItem } from '../layout/projectStore.ts';
import { itemName } from '../modShare/ops.ts';
import { bridges } from '../opbridge/store.ts';
import type { ControllerControl, ControllerRegistry, ControllerUnit } from '../opbridge/httpClient.ts';
import { readRegistry, kindWord, type RegistryRead } from '../opbridge/controllers.ts';
import { onControlEvent } from '../opbridge/consumerWs.ts';
import {
  assignableActions, assign, unassign, bindingsForItem, fits, isItemAction, type AssignableAction,
} from '../controllers/assign.ts';
import { Tip, TipRow } from './Tip.tsx';

interface Located { unit: ControllerUnit; control: ControllerControl }

function findControl(reg: ControllerRegistry, controlId: string): Located | null {
  for (const unit of reg.units) {
    const control = unit.controls.find((c) => c.id === controlId);
    if (control) return { unit, control };
  }
  return null;
}

/** "Nektar Pacer" - what the unit IS, for people. */
function unitWords(u: ControllerUnit): string {
  return u.name?.trim() || u.modelName || u.model || u.code;
}

/** The computer a unit is plugged into, by our own name for it. */
function machineOf(u: ControllerUnit): string | null {
  for (const p of u.ports) {
    const b = bridges().find((x) => x.nodeId === p.nodeId);
    if (b) return b.name;
  }
  return null;
}

/** What an action id belongs to, in words: one of our items by name, or
 *  something another app or the dashboard set. */
function doerOf(actionId: string): string {
  for (const p of projects()) {
    for (const pg of p.pages) {
      const it = pg.items.find((i) => isItemAction(i.id, actionId, i));
      if (it) return `"${itemName(it)}"`;
    }
  }
  return 'something set in OPBridge or another app';
}

/** "footswitch 3" from `NP1-P3` and its kind. */
function controlWords(c: ControllerControl): string {
  const n = /(\d+)$/.exec(c.code)?.[1];
  const base = c.name?.trim() || `${kindWord(c.kind)}${n ? ` ${n}` : ''}`;
  return base;
}

export function AssignControllerField(props: { item: LayoutItem }) {
  const [refresh, setRefresh] = createSignal(0);
  const [reg] = createResource(() => [props.item.id, refresh()] as const, () => readRegistry());
  const [actions] = createResource(() => [props.item.id, refresh()] as const, () => assignableActions(props.item));
  const [open, setOpen] = createSignal(false);
  const [busy, setBusy] = createSignal<string | null>(null);
  const [error, setError] = createSignal<string | null>(null);

  const okReg = (): (RegistryRead & { ok: true }) | null => { const r = reg(); return r && r.ok ? r : null; };
  const mine = createMemo(() => { const r = okReg(); return r ? bindingsForItem(r.registry, props.item.id, props.item) : []; });
  const labelFor = (actionId: string, input?: string): string => {
    const a = actions();
    if (!a || !a.ok) return actionId;
    const hit = a.actions.find((x) => x.actionId === actionId && (x.input ?? undefined) === (input ?? undefined))
      ?? a.actions.find((x) => x.actionId === actionId);
    return hit?.label ?? 'Something this no longer does';
  };
  const pageName = (pageId: string): string | null => {
    const r = okReg();
    if (!r || r.registry.pages.length < 2) return null;
    return r.registry.pages.find((p) => p.id === pageId)?.name ?? pageId;
  };

  async function remove(b: ReturnType<typeof mine>[number]): Promise<void> {
    setBusy(b.controlId + b.pageId);
    setError(null);
    try {
      await unassign(props.item, b, okReg()?.bridgeId);
    } catch (e) {
      setError(`Could not remove it: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(null);
      setRefresh((n) => n + 1);
    }
  }

  return (
    <div class="inspector-zone" data-assign-controller>
      <div class="inspector-pills">
        <span class="inspector-pills-label">Controller<Show when={!actions.loading && actions() && !actions()!.ok && okReg()}>
          <Tip text={(actions() as { ok: false; why: string }).why} />
        </Show></span>
        {/* Why it cannot be assigned is an explanation, so it sits behind the ? (DEC-318); the row says "Not yet". */}
        <Show when={!actions.loading && actions() && !actions()!.ok && okReg()}>
          <span class="inspector-pills-note" data-assign-status="actions">Not yet</span>
        </Show>
        <Show when={actions()?.ok && okReg()}>
          <button type="button" class="inspector-pill" data-assign-open onClick={() => { setError(null); setOpen(true); }}>
            Assign a controller...
          </button>
        </Show>
      </div>
      <Show when={reg.loading || actions.loading}>
        <span class="inspector-hint">Asking OPBridge...</span>
      </Show>
      <Show when={!reg.loading && reg() && !reg()!.ok}>
        <span class="inspector-hint" data-assign-status="registry">{(reg() as RegistryRead & { ok: false }).detail}</span>
      </Show>
      <Show when={mine().length > 0}>
        <div class="assign-list">
          <For each={mine()}>
            {(b) => {
              const loc = () => { const r = okReg(); return r ? findControl(r.registry, b.controlId) : null; };
              return (
                <div class="assign-row" data-assign-binding={b.controlId}>
                  <span class="assign-code">{loc()?.control.code ?? 'A removed control'}</span>
                  <span class="assign-what">
                    {labelFor(b.actionId, b.input)}
                    <Show when={pageName(b.pageId)}>{(n) => <small> on {n()}</small>}</Show>
                  </span>
                  <button
                    type="button" class="inspector-pill" data-assign-remove
                    disabled={busy() !== null}
                    onClick={() => void remove(b)}
                    title="Take this off the controller. The controller itself stays in OPBridge."
                  >
                    {busy() === b.controlId + b.pageId ? 'Removing...' : 'Remove'}
                  </button>
                </div>
              );
            }}
          </For>
        </div>
      </Show>
      <Show when={mine().length === 0 && okReg() && actions()?.ok}>
        <TipRow label="None yet" text="A pedal or pad that OPBridge knows can do this, even with this screen asleep." />
      </Show>
      <Show when={error()}>
        <span class="inspector-hint" data-assign-status="error">{error()}</span>
      </Show>
      <Show when={open() && okReg() && actions()?.ok}>
        <ControllerPicker
          item={props.item}
          registry={okReg()!.registry}
          viaBridgeId={okReg()!.bridgeId}
          actions={(actions() as { ok: true; actions: AssignableAction[] }).actions}
          onClose={(changed) => { setOpen(false); if (changed) setRefresh((n) => n + 1); }}
        />
      </Show>
    </div>
  );
}

function ControllerPicker(props: {
  item: LayoutItem;
  registry: ControllerRegistry;
  viaBridgeId: string;
  actions: AssignableAction[];
  onClose: (changed: boolean) => void;
}) {
  const [reg, setReg] = createSignal(props.registry);
  const [actionKey, setActionKey] = createSignal(props.actions[0]!.key);
  const action = () => props.actions.find((a) => a.key === actionKey()) ?? props.actions[0]!;
  const [picked, setPicked] = createSignal<string | null>(null);
  const [pageId, setPageId] = createSignal(props.registry.activePageId ?? props.registry.pages[0]?.id ?? 'page-1');
  const [note, setNote] = createSignal<string | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [failed, setFailed] = createSignal<string | null>(null);
  const [openUnit, setOpenUnit] = createSignal<string | null>(null);

  const pickedLoc = () => { const id = picked(); return id ? findControl(reg(), id) : null; };
  /** What the picked control does on that page now, if it is something else. */
  const replaces = () => {
    const id = picked();
    if (!id) return null;
    const b = reg().bindings.find((x) => x.pageId === pageId() && x.controlId === id);
    if (!b || b.actionId === action().actionId) return null;
    return b;
  };

  // Changing what it should do can make the picked control unfit.
  const choose = (key: string) => {
    setActionKey(key);
    const loc = pickedLoc();
    if (loc && !fits(loc.control, action())) setPicked(null);
  };

  // PRESS IT. One press can arrive on several sockets (presses cross the
  // mesh), so a repeat of the same control within 400 ms is the same press.
  let lastId = '';
  let lastAt = 0;
  const off = onControlEvent((e) => {
    if (e.state === 'release') return;
    const now = Date.now();
    if (e.controlId === lastId && now - lastAt < 400) return;
    lastId = e.controlId;
    lastAt = now;
    void (async () => {
      let loc = findControl(reg(), e.controlId);
      if (!loc) {
        // A control OPBridge learned on this very press (S309): read again.
        const r = await readRegistry();
        if (r.ok) { setReg(r.registry); loc = findControl(r.registry, e.controlId); }
      }
      if (!loc) { setNote(`${e.code || 'That control'} is not in OPBridge's list yet. Press it again.`); return; }
      if (!fits(loc.control, action())) {
        const w = kindWord(loc.control.kind);
        setNote(`${loc.control.code} is ${/^[aeiou]/.test(w) ? 'an' : 'a'} ${w}, which cannot do "${action().label}". ${action().input === null ? 'Press a switch or button.' : 'Move a knob, fader or expression pedal.'}`);
        return;
      }
      setNote(null);
      setPicked(loc.control.id);
      setOpenUnit(loc.unit.id);
    })();
  });
  onCleanup(off);

  function onKey(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    props.onClose(false);
  }
  window.addEventListener('keydown', onKey, true);
  onCleanup(() => window.removeEventListener('keydown', onKey, true));

  async function doAssign(): Promise<void> {
    const loc = pickedLoc();
    if (!loc) return;
    setBusy(true);
    setFailed(null);
    try {
      await assign(props.item, action(), loc.control, pageId(), props.viaBridgeId);
      props.onClose(true);
    } catch (e) {
      setFailed(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const units = () => reg().units;

  // On <body>, not shellPortal, on purpose (see .nv-sheet-scrim): it opens from the Inspector, which lives outside
  // `.shell`, so a sheet inside the shell would sit UNDER it (measured 2026-11-03, audit F-11: its rows unclickable).
  return (
    <Portal>
      <div class="nv-sheet-scrim" onClick={() => props.onClose(false)} role="dialog" aria-modal="true" aria-label="Assign a controller">
        <div class="nv-sheet assign-sheet" onClick={(e) => e.stopPropagation()}>
          <div class="nv-sheet-h">
            <h2>Assign a controller</h2>
            <p>Press a pedal, pad or knob on any controller OPBridge knows, or pick one below.</p>
          </div>
          <div class="nv-sheet-b">
            <Show when={props.actions.length > 1}>
              <div class="nv-ss">
                <div class="nv-ss-h"><h2>It should</h2></div>
                <select class="nv-select" data-assign-action value={actionKey()} onChange={(e) => choose(e.currentTarget.value)}>
                  <For each={props.actions}>{(a) => <option value={a.key}>{a.label}</option>}</For>
                </select>
              </div>
            </Show>

            <div class="nv-ss">
              <div class="nv-ss-h"><h2>Control</h2></div>
              <div class="nv-note" data-tone="info" data-assign-listening>
                <i class="nv-dot" data-s={picked() ? 'ok' : 'live'} aria-hidden="true" />
                <span>
                  <Show when={pickedLoc()} fallback={action().input === null ? 'Waiting for a press...' : 'Waiting for a knob or pedal to move...'}>
                    {(l) => <><b>{l().control.code}</b> {unitWords(l().unit)}, {controlWords(l().control)}</>}
                  </Show>
                </span>
              </div>
              <Show when={note()}><div class="nv-note" data-tone="warn" data-assign-note><span>{note()}</span></div></Show>
              <div class="nav-well assign-units">
                <For each={units()}>
                  {(u) => {
                    const usable = () => u.controls.filter((c) => fits(c, action()));
                    const where = () => machineOf(u);
                    const isOpen = () => openUnit() === u.id;
                    return (
                      <>
                        <button
                          type="button" class="nav-lr nav-lr-button" data-assign-unit={u.code}
                          aria-expanded={isOpen() ? 'true' : 'false'}
                          onClick={() => setOpenUnit(isOpen() ? null : u.id)}
                        >
                          <span class="nav-lr-main">
                            <b>{u.code} - {unitWords(u)}</b>
                            <small>
                              {u.controls.length === 0
                                ? 'No controls yet: press one on it and it appears.'
                                : `${usable().length} of ${u.controls.length} can do this`}
                              <Show when={where()}>{(w) => <> - on {w()}</>}</Show>
                            </small>
                          </span>
                          <span class="nav-lr-chev" aria-hidden="true">{isOpen() ? '▾' : '▸'}</span>
                        </button>
                        <Show when={isOpen()}>
                          <div class="nv-chips">
                            <For each={usable()}>
                              {(c) => (
                                <button
                                  type="button" data-assign-control={c.code}
                                  aria-pressed={picked() === c.id ? 'true' : 'false'}
                                  onClick={() => { setNote(null); setPicked(c.id); }}
                                  title={controlWords(c)}
                                >
                                  {c.code.replace(`${u.code}-`, '')}
                                </button>
                              )}
                            </For>
                          </div>
                        </Show>
                      </>
                    );
                  }}
                </For>
              </div>
            </div>

            <Show when={reg().pages.length > 1}>
              <div class="nv-ss">
                <div class="nv-ss-h"><h2>Page</h2><p>OPBridge's controller pages. The active one is picked.</p></div>
                <select class="nv-select" data-assign-page value={pageId()} onChange={(e) => setPageId(e.currentTarget.value)}>
                  <For each={reg().pages}>{(p) => <option value={p.id}>{p.name ?? p.id}{p.id === reg().activePageId ? ' (active)' : ''}</option>}</For>
                </select>
              </div>
            </Show>

            <Show when={replaces()}>
              {(b) => (
                <div class="nv-note" data-tone="warn" data-assign-replaces>
                  <span>{pickedLoc()?.control.code} already does {doerOf(b().actionId)} on this page. Assigning replaces it.</span>
                </div>
              )}
            </Show>
            <Show when={failed()}>
              <div class="nv-note" data-tone="bad" data-assign-failed><span>{failed()}</span></div>
            </Show>
          </div>
          <div class="nv-sheet-f nv-row">
            <span class="nv-sp" />
            <button type="button" class="nv-b nv-b-q" onClick={() => props.onClose(false)}>Cancel</button>
            <button type="button" class="nv-b nv-b-pri" data-assign-confirm disabled={!picked() || busy()} onClick={() => void doAssign()}>
              {busy() ? 'Assigning...' : 'Assign'}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}
